import PatternValuable from './base/pattern-valuable'

export default class Procedure extends PatternValuable {
  constructor (pattern) {
    super(pattern, 'procedure')
  }

  async getValue({ character, raw, ui, context }) {
    return async () => await this.run({ character, raw, ui, context })
  }
}

Procedure.BasicProcedure = class BasicProcedure extends Procedure {
  constructor (pattern, executeWithParams) {
    super(pattern)
    this.executeWithParams = executeWithParams
  }

  async run ({ character, raw, ui, context }) {
    const params = await this.deriveParams({ character, raw, ui, context })
    const rawParams = this.deriveRawParams(raw)
    await this.executeWithParams(params, { character, ui, context, rawParams })
  }
}

Procedure.ComplexProcedure = class ComplexProcedure extends Procedure {
  constructor (pattern, meaning) {
    super(pattern)
    this.meaning = meaning
  }

  async run({ character, raw, ui, context }) {
    context = Object.assign({}, context, await this.deriveParams({ character, raw, ui, context }))
    await character.execute(this.meaning, ui, context)
  }
}

Procedure.PRESETS = Object.entries({
  // character data procedures
  die: async (params, { character }) => await character.die(),
  revive: async (params, { character }) => await character.revive(),
  'set <%field%> = <any:value>': async ({ field, value }, { character }) => await character.set(field, value),
  'add <any:value> to <%field%>': async ({ field, value }, { character }) => await character.addToField(field, value),
  'remove <any:value> from <%field%>': async ({ field, value }, { character }) => await character.removeFromField(field, value),
  'toggle <%field%>': async ({ field }, { character }) => { await character.toggle(field) },
  'modify <number:count=-1> <%text:filter%> <any:value>': async ({ filter, value, count }, { character }) => { await character.addModifier({ filter, value, count }) },

  // UI procedures
  'show <any:text> under <text:title>': ({ text, title }, { ui }) => ui.output(text, title),
  'show <any:text>': ({ text }, { ui, rawParams }) => ui.output(text, rawParams.text),
  'choose <number:count=1> <text=Choose> {choices}': async ({ text, count, choices }, { ui, character, context }) => {
    if (choices === 'null')   return

    const selections = await ui.choose(text, Object.keys(choices), count)
    for(const selection of selections) {
      await character.execute(choices[selection], ui, context)
    }

    // Handling negative choices
    const notChosenSelections = Object.entires(choices)
      .filter(([key, value]) => !selections.includes(key) && (value.constructor === String ? value : Obejct.keys(value)[0]).startsWith('dont'))
      .map((key) => key)
    for(const notChosenSelection of notChosenSelections) {
      await character.execute(choices[notChosenSelection], ui, context)
    }
  },
  'options {choices}': async ({ choices }, { ui, character }) => {
    const originalChoice = character.history.last(event => event.type === 'exec' && event.name.startsWith('choose'))

    const selections = await ui.choose(text, Object.keys(choices), count)
    for(const selection of selections) {
      await character.execute(choices[selection], ui, context)
    }

    // Handling negative choices
    const notChosenSelections = Object.entires(choices)
      .filter(([key, value]) => !selections.includes(key) && (value.constructor === String ? value : Obejct.keys(value)[0]).startsWith('dont'))
      .map((key) => key)
    for(const notChosenSelection of notChosenSelections) {
      await character.execute(choices[notChosenSelection], ui, context)
    }
  },

  // logical flow procedures
  'switch [first] <number:value> {outcomes}': async ({ value, outcomes, first }, { ui, character, context }) => {
    for (const rawOutcome of outcomes) {
      const outcome = await character.getValue({ raw: rawOutcome, ui, context })
      if (outcome === true || (outcome.includes && outcome.includes(value)) || outcome === value) {
        await character.execute(outcomes[option], ui, context)
        if (first) break
      }
    }
  },
  'is <boolean:condition> {outcomes}': async ({ condition, outcomes }, { ui, character, context }) => {
    await character.execute(condition ? outcomes.yes : outcomes.no, ui, context)
  },
  'repeat <number:count> times {procedure}': async ({ count, procedure }, { ui, character, context }) => {
    for(let i = 0; i < count; i++)
      await character.execute(procedure, ui, context)
  },
  'for <%text:itemName%> in <array> where <%text:filter%> {procedure}': async ({ itemName, array, filter, procedure }, { ui, character, context }) => {
    for (const item of array) {
      const innerContext = Object.assign({}, context, { [itemName]: item })
      const passFilter = await character.get(filter, { ui, context: innerContext, type: 'boolean' })
      if (passFilter) {
        await character.execute(procedure, ui, innerContext)
      }
    }
  },
  'for <%text:itemName%> in <array> {procedure}': async ({ itemName, array, procedure }, { ui, character, context }) => {
    for (const item of array) {
      const innerContext = Object.assign({}, context, { [itemName]: item })
      await character.execute(procedure, ui, innerContext)
    }
  },
  'either {options}': async ({ options }, { ui, character, context }) => {
    for (const option of options) {
      if (option) {
        await character.execute(option, ui, context)
        break
      }
    }
  },
  'execute <%procedure%>': async ({ procedure }, { ui, character, context }) => {
    procedure = await character.getRawValue({ raw: procedure, ui, context, fallback: 'do nothing' })
    await character.execute(procedure, ui, context)
  },
  'define <%text:name%> to be <any:value>': async ({ name, value }, { context }) => {
    Object.assign(context, { [name]: value })
  },
  '<procedure:first> then <procedure:second>': async ({ first, second }, { ui, character, context }) => {
    await character.execute(first, ui, context)
    await character.execute(second, ui, context)
  },
  'do nothing': () => {}
}).map(([pattern, executeWithParams]) => new Procedure.BasicProcedure(pattern, executeWithParams))
